import React, { FC, useState } from 'react';
import List, { ListProps } from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import { CustomFileActions, FilesPropType } from '../FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { Button, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import translate from '@translate';
import FileDisplay from '../FileDisplay';
import { downloadFile } from './functions';
import { isArchiveMode } from 'src/utils/archiveMode';
import { colors, sizes } from 'src/themes/default';
import { FileType } from 'src/types/file';
import Tooltip from '../Tooltip';
import { EyeIcon, TrashIcon } from '../SkladonIcon/SmallIcons';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

export interface DisplayFilesPropsType {
    files?: FilesPropType;
    height: number;
    onDeleteFile?: (file: FileType) => void;
    disableShowFile?: boolean;
    disableDowloadFile?: boolean;
    customFileActions?: CustomFileActions;
}

const FilesList = styled(List)<ListProps>(({ theme }) => ({
    display: 'inline-block',
    overflow: 'auto',
    width: `calc(100% - ${sizes.file_input_W})`,

    '& .MuiListItemText-root .MuiTypography-body1': {
        maxWidth: '100%',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    },

    [theme.breakpoints.up('sm')]: {
        '& .MuiListItemText-root .MuiTypography-body1': {
            maxWidth: 'calc(100% - 150px)',
        },
    },
    [theme.breakpoints.down('sm')]: {
        display: 'block',
        width: '100%',

        '& .MuiListItem-root': {
            display: 'block',
            paddingBottom: '0px',
        },
        '& .MuiListItemSecondaryAction-root': {
            position: 'relative',
            right: 'unset',
            padding: '0px 10px',
            transform: 'unset',
        },
    },
}));

const DisplayFiles: FC<DisplayFilesPropsType> = (props) => {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [showFile, setShowFile] = useState<FileType | null>(null);

    const handleShowFile = (file: FileType) => {
        setShowFile(file);
    };

    const handleHideFile = () => {
        setShowFile(null);
    };

    const handleDowloadFile = (file: FileType) => {
        downloadFile(file, undefined, isArchiveMode());
    };

    return (
        <FilesList sx={{ minHeight: props.height }}>
            {(props.files || []).map((file, key: number) => {
                const rowKey = file.id ?? `${file.fileName || ''}-${key}`;

                return (
                    <div key={rowKey}>
                        {key !== 0 && <Divider />}
                        <ListItem alignItems="flex-start">
                            <ListItemText primary={file?.name || file?.fileName} />
                            <ListItemSecondaryAction>
                                {props.customFileActions && props.customFileActions(file)}
                                {!props.disableDowloadFile && (
                                    <Tooltip title={translate('file.download')}>
                                        <IconButton onClick={() => handleDowloadFile(file)}>
                                            <DownloadIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {!props.disableShowFile && (
                                    <Tooltip title={translate('file.display')}>
                                        <IconButton onClick={() => handleShowFile(file)}>
                                            <EyeIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {props.onDeleteFile ? (
                                    mobile ? (
                                        <Tooltip title={translate('file.delete')}>
                                            <IconButton
                                                sx={{ '& svg path': { fill: colors.primary_O } }}
                                                onClick={() => props.onDeleteFile?.(file)}
                                            >
                                                <TrashIcon />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Button onClick={() => props.onDeleteFile && props.onDeleteFile(file)}>
                                            {translate('file.delete')}
                                        </Button>
                                    )
                                ) : null}
                            </ListItemSecondaryAction>
                        </ListItem>
                    </div>
                );
            })}
            {showFile && <FileDisplay file={showFile} modal onClose={handleHideFile} />}
        </FilesList>
    );
};

export default DisplayFiles;
